import React, { createContext, useCallback, useContext, useState } from "react";
import { markAllOnboardingSkipped, markTourDone, resetAllToursForUser } from "./storageHelper";
import { trackOnboardingEvent } from "./trackEvent";

const TourContext = createContext(null);

function resolveEffectiveUserId(userId) {
  if (userId) return userId;

  try {
    const raw = localStorage.getItem("currentUser");
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.id || null;
  } catch {
    return null;
  }
}

export function TourProvider({ children, userId }) {
  const [runCatalogTour, setRunCatalogTour] = useState(false);
  const [runVehicleTour, setRunVehicleTour] = useState(false);
  const [runOffersTour, setRunOffersTour] = useState(false);
  const [manualTourName, setManualTourName] = useState(null);
  const [stepIndex, setStepIndexInternal] = useState(0);
  const [isCatalogReady, setIsCatalogReady] = useState(false);

  // Wrapper עם debug ל-setStepIndex
  const setStepIndex = useCallback((newIndex) => {
    if (process.env.NODE_ENV === "development") {
      console.debug(`[TourContext] setStepIndex: -> ${newIndex}`);
    }
    setStepIndexInternal(newIndex);
  }, []);

  const startCatalogTour = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      console.debug("[TourContext] Starting catalog tour, resetting stepIndex to 0");
    }
    setStepIndexInternal(0);
    setManualTourName(null);
    setRunCatalogTour(false);
    setTimeout(() => {
      setRunCatalogTour(true);
    }, 300);
    trackOnboardingEvent("tour_started", { tourName: "catalog" });
  }, []);

  const startVehicleTour = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      console.debug("[TourContext] Starting vehicle tour, resetting stepIndex to 0");
    }
    setStepIndexInternal(0);
    setManualTourName(null);
    setRunVehicleTour(true);
    trackOnboardingEvent("tour_started", { tourName: "vehicle" });
  }, []);

  const startOffersTour = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      console.debug("[TourContext] Starting offers tour, resetting stepIndex to 0");
    }
    setStepIndexInternal(0);
    setManualTourName(null);
    setRunOffersTour(true);
    trackOnboardingEvent("tour_started", { tourName: "offers" });
  }, []);

  const startOnboardingTourManually = useCallback((tourName = "catalog") => {
    if (process.env.NODE_ENV === "development") {
      console.debug(`[TourContext] Manually starting ${tourName} tour`);
    }

    setStepIndexInternal(0);
    setManualTourName(tourName);
    setRunCatalogTour(tourName === "catalog");
    setRunVehicleTour(tourName === "vehicle");
    setRunOffersTour(tourName === "offers");
    trackOnboardingEvent("tour_manually_started", { tourName });
  }, []);

  const isManualTour = useCallback((tourName) => manualTourName === tourName, [manualTourName]);

  const endTour = useCallback((tourName, overrideUserId, status = "finished") => {
    if (process.env.NODE_ENV === "development") {
      console.debug(`[TourContext] Ending ${tourName} tour`);
    }

    const effectiveUserId = overrideUserId || resolveEffectiveUserId(userId);
    const wasSkipped = status === "skipped";

    if (tourName === "catalog") {
      setRunCatalogTour(false);
    } else if (tourName === "vehicle") {
      setRunVehicleTour(false);
    } else if (tourName === "offers") {
      setRunOffersTour(false);
    }

    if (wasSkipped) {
      markAllOnboardingSkipped(effectiveUserId);
    } else if (tourName === "catalog") {
      markTourDone("catalog", effectiveUserId);
    } else if (tourName === "vehicle") {
      markTourDone("vehicle", effectiveUserId);
    } else if (tourName === "offers") {
      markTourDone("offers", effectiveUserId);
    }
    setManualTourName(null);
    setStepIndexInternal(0);
  }, [userId]);

  // איפוס מלא — לכפתור "הפעל סיור מחדש" בתפריט עזרה
  const resetAllTours = useCallback(() => {
    const effectiveUserId = resolveEffectiveUserId(userId);
    resetAllToursForUser(effectiveUserId);
    setRunCatalogTour(false);
    setRunVehicleTour(false);
    setRunOffersTour(false);
    setManualTourName(null);
    setStepIndexInternal(0);
  }, [userId]);

  return (
    <TourContext.Provider
      value={{
        runCatalogTour,
        runVehicleTour,
        runOffersTour,
        stepIndex,
        setStepIndex,
        isCatalogReady,
        setIsCatalogReady,
        startCatalogTour,
        startVehicleTour,
        startOffersTour,
        startOnboardingTourManually,
        isManualTour,
        endTour,
        resetAllTours,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
}
